import React from "react";
import { IoCloseOutline } from "react-icons/io5";

interface DeactivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "user" | "center" | "parent" | null;
}

const DeactivationModal: React.FC<DeactivationModalProps> = ({
  isOpen,
  onClose,
  type,
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen || !type) return null;

  // A center-added parent is reactivated by their center's admins, not by
  // platform support — so that variant points them there instead.
  const isParentDeactivated = type === "parent";
  const title =
    type === "center" ? "Center Deactivated" : "Account Deactivated";
  const message =
    type === "user"
      ? "Your account has been deactivated. To reactivate your account, please contact our customer support team."
      : isParentDeactivated
        ? "Your account has been deactivated. Please contact your center."
        : "The center associated with your account has been deactivated. To reactivate the center, please contact your organization or our customer support team.";

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 h-screen py-3">
      <div
        ref={modalRef}
        className="bg-white p-8 rounded-lg shadow-lg w-full max-w-[500px]"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-2xl md:text-3xl text-secondary font-semibold">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-secondary hover:text-red-500 transition-colors"
          >
            <IoCloseOutline size={28} />
          </button>
        </div>

        <p className="text-base md:text-lg text-secondaryVariant mb-6 leading-relaxed">
          {message}
        </p>

        {!isParentDeactivated && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-secondaryVariant font-medium">
              📧 Email us:{" "}
              <a
                href="mailto:support@childcarefinder.com"
                className="text-secondary font-semibold hover:underline"
              >
                support@childcarefinder.com
              </a>
            </p>
            <p className="text-sm text-secondaryVariant font-medium">
              📞 Call us:{" "}
              <a
                href="tel:+18005551234"
                className="text-secondary font-semibold hover:underline"
              >
                1-800-555-1234
              </a>
            </p>
          </div>
        )}

        <button onClick={onClose} className="mt-8 w-full btnPrimary">
          Understood
        </button>
      </div>
    </div>
  );
};

export default DeactivationModal;
